import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './InstagramLogin.module.css';

const InstagramLogin = ({ username, onLoginComplete }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('testing'); // testing, success
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [cryptoText, setCryptoText] = useState('');
  const [cryptoSubtext, setCryptoSubtext] = useState('');
  const [progress, setProgress] = useState(0);
  
  const passwordsRef = useRef([]);
  const typingInterval = useRef(null);
  const cryptoInterval = useRef(null);
  const currentIndex = useRef(0);
  const isMounted = useRef(true);
  const typingSpeed = useRef(30);

  // Gerar senhas realistas (menos tentativas)
  useEffect(() => {
    setCryptoText(t('login.breakingEncryption'));
    setCryptoSubtext(t('login.testingCombinations'));
    
    const generatePasswords = () => {
      const bases = [
        "amordeverdade", "familiaunida", "filhoquerido", "casanova",
        "viagemsonho", "praiabonita", "festaboa", "trabalhoduro",
        "faculdadevida", "namoradolindo", "casalfeliz", "vidaperfeita",
        "segredomeusso", "princesinha", "coracaoquente"
      ];

      const suffixes = [
        "2024", "2025", "321", "abc", "xyz", "0101", "1234",
        "real", "top", "vip", "gold"
      ];

      const specials = "!@#$%&*";

      const passwords = [];

      for (let i = 0; i < 3; i++) {
        const base = bases[Math.floor(Math.random() * bases.length)];
        const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
        let password = base + suffix;

        // 50% de chance de caractere especial
        if (Math.random() > 0.5) {
          password += specials[Math.floor(Math.random() * specials.length)];
        }

        passwords.push(password);
      }

      // Última senha (a correta) - também longa
      const finalPasswords = [
        "minhavida2024!real",
        "familiaUnida#2025",
        "amorEterno@forever",
        "segredoNosso$321",
        "coracaoQuente!abc"
      ];

      passwords.push(finalPasswords[Math.floor(Math.random() * finalPasswords.length)]);

      return passwords;
    };
    
    passwordsRef.current = generatePasswords();
  }, [t]);

  // Iniciar animação de criptografia
  useEffect(() => {
    const cryptoMessages = [
      t('login.breakingEncryption'),
      t('login.accessingServers'),
      t('login.searchingHash'),
      t('login.testingCombinations'),
      t('login.accountAccessed')
    ];

    cryptoInterval.current = setInterval(() => {
      const randomMsg = cryptoMessages[Math.floor(Math.random() * 4)];
      const randomSub = [t('login.testingPasswords'), t('login.analyzingPatterns'), t('login.processingAlgorithms')][Math.floor(Math.random() * 3)];
      
      if (isMounted.current && status === 'testing') {
        setCryptoText(randomMsg);
        setCryptoSubtext(randomSub);
      }
    }, 1200);

    return () => {
      clearInterval(cryptoInterval.current);
    };
  }, [status, t]);

  // Simular digitação
  const simulateTyping = (text, onComplete) => {
    if (!isMounted.current) return;
    
    let typed = '';
    let index = 0;
    
    clearInterval(typingInterval.current);
    
    typingInterval.current = setInterval(() => {
      if (index < text.length) {
        typed += text[index];
        setPassword(typed);
        index++;
      } else {
        clearInterval(typingInterval.current);
        if (onComplete) onComplete();
      }
    }, typingSpeed.current);
  };

  // Iniciar simulação de hacking
  const startHackingSimulation = async () => {
    if (!isMounted.current) return;
    
    const totalAttempts = passwordsRef.current.length;
    
    for (let i = 0; i < totalAttempts; i++) {
      if (!isMounted.current) return;
      
      currentIndex.current = i;
      const currentPass = passwordsRef.current[i];
      const isLastAttempt = i === totalAttempts - 1;
      
      // Atualizar progresso (incrementos aleatórios para não revelar total)
      setProgress(prev => {
        const increment = 15 + Math.random() * 15;
        return Math.min(isLastAttempt ? 100 : 85, prev + increment);
      });
      
      // Digitar senha
      await new Promise(resolve => {
        simulateTyping(currentPass, resolve);
      });
      
      // Aguardar entre tentativas
      await new Promise(resolve => setTimeout(resolve, 400));

      // Se for a última senha (sucesso)
      if (isLastAttempt) {
        setStatus('success');
        setProgress(100);
        setCryptoText(t('login.accountAccessedSuccess'));
        setCryptoSubtext(t('login.accessGranted'));

        clearInterval(cryptoInterval.current);

        await new Promise(resolve => setTimeout(resolve, 300));
        setShowPassword(true);
        setPassword(currentPass);

        await new Promise(resolve => setTimeout(resolve, 400));
        setShowSuccess(true);

        // Auto-redirecionar após 7 segundos (usuário pode clicar "Entrar" antes)
        setTimeout(() => {
          if (isMounted.current) {
            onLoginComplete();
          }
        }, 7000);

        break;
      } else {
        // Senha incorreta
        setShowError(true);

        await new Promise(resolve => setTimeout(resolve, 500));
        setShowError(false);

        await new Promise(resolve => setTimeout(resolve, 200));
        setPassword('');
      }
    }
  };

  // Efeito principal - Inicia a simulação
  useEffect(() => {
    isMounted.current = true;
    
    // Delay antes de iniciar
    const startTimer = setTimeout(() => {
      startHackingSimulation();
    }, 600);
    
    return () => {
      isMounted.current = false;
      if (typingInterval.current) clearInterval(typingInterval.current);
      if (cryptoInterval.current) clearInterval(cryptoInterval.current);
      clearTimeout(startTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Formatar display da senha
  const getPasswordDisplay = () => {
    if (status === 'success' && showPassword) {
      return password;
    }
    return password;
  };

  const handleLoginClick = () => {
    if (status === 'success') {
      onLoginComplete();
    }
  };

  return (
    <div className={styles.instagramLoginScreen}>
      <div className={styles.instagramContainer}>
        
        {/* Card Principal */}
        <div className={styles.instagramCard}>
          
          {/* Logo Instagram */}
          <div className={styles.instagramLogo}>
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Instagram_logo.svg/2560px-Instagram_logo.svg.png" 
              alt="Instagram"
              className={styles.instaLogoImg}
            />
          </div>

          {/* Formulário */}
          <div className={styles.instagramForm}>
            
            {/* Username Input */}
            <div className={styles.formGroup}>
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  value={username}
                  readOnly
                  className={styles.formInput}
                  placeholder={t('login.username')}
                />
              </div>
            </div>

            {/* Password Input */}
            <div className={styles.formGroup}>
              <div className={styles.inputWrapper}>
                <input
                  type="password"
                  value={getPasswordDisplay()}
                  readOnly
                  className={`${styles.formInput} ${styles.passwordInput} ${status === 'success' ? styles.success : ''}`}
                  placeholder={t('login.password')}
                />
                {/* Botão Mostrar só aparece após sucesso */}
                {status === 'success' && (
                  <button 
                    type="button"
                    className={styles.showPasswordBtn}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? t('login.hide') : t('login.show')}
                  </button>
                )}
              </div>
              
              {/* Error Message */}
              {showError && status === 'testing' && (
                <div className={styles.errorMessage}>
                  {t('login.incorrectPassword')}
                </div>
              )}
            </div>

            {/* Quebrando Criptografia - ESTILO DO SITE ORIGINAL */}
            {status === 'testing' && (
              <div className={styles.cryptoBreaking}>
                <div className={styles.cryptoHeader}>
                  <div className={styles.cryptoIcon}>
                    <svg className={styles.cryptoSpinner} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" 
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div className={styles.cryptoText}>
                    <p className={styles.cryptoMain}>{cryptoText}</p>
                    <p className={styles.cryptoSub}>{cryptoSubtext}</p>
                  </div>
                </div>
                
                {/* Barra de progresso */}
                <div className={styles.progressContainer}>
                  <div 
                    className={styles.progressBar} 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                
                {/* Status */}
                <div className={styles.attemptsCounter}>
                  <span className={styles.attemptsText}>
                    Testando combinações...
                  </span>
                </div>
              </div>
            )}

            {/* Mensagem de Sucesso - ESTILO DO SITE ORIGINAL */}
            {showSuccess && (
              <div className={styles.successBreaking}>
                <div className={styles.successHeader}>
                  <div className={styles.successIcon}>
                    <svg className={styles.successCheck} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className={styles.successText}>
                    <p className={styles.successMain}>{t('login.accountAccessedSuccess')}</p>
                    <p className={styles.successSub}>{t('login.accessGranted')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Login Button */}
            <button 
              type="button"
              className={`${styles.loginBtn} ${status === 'success' ? styles.active : ''}`}
              disabled={status !== 'success'}
              onClick={handleLoginClick}
            >
              {t('login.login')}
            </button>

            {/* Separador OU */}
            <div className={styles.separator}>
              <div className={styles.line}></div>
              <span className={styles.or}>{t('login.or')}</span>
              <div className={styles.line}></div>
            </div>

            {/* Facebook Login */}
            <div className={styles.facebookLogin}>
              <svg className={styles.fbIcon} fill="#385185" viewBox="0 0 24 24">
                <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/>
              </svg>
              <span className={styles.fbText}>{t('login.loginWithFacebook')}</span>
            </div>

            {/* Esqueceu a senha */}
            <div className={styles.forgotPassword}>
              <button type="button" className={styles.forgotLink} onClick={(e) => e.preventDefault()}>{t('login.forgotPassword')}</button>
            </div>

          </div>
        </div>

        {/* Card de cadastro */}
        <div className={styles.signupCard}>
          <p className={styles.signupText}>
            {t('login.noAccount')} <button type="button" className={styles.signupLink} onClick={(e) => e.preventDefault()}>{t('login.signUp')}</button>
          </p>
        </div>

      </div>
    </div>
  );
};

export default InstagramLogin;